import { useEffect, useState, useRef } from "react";
import { Modal } from "react-bootstrap";
import { useForm } from "react-hook-form";
import Select, { createFilter } from "react-select";
import OptionsType from "react-select";
import { AssetSvg } from "../../assets";
import { FormErrorMessage } from "../../components";
import { reactSelectFilterConfig } from "../../configs";
import { InputFieldValidationErrorMessage, topLoaderProgress } from "../../configs/appConstants";
import { ApiSchemaCreateLanguagePack, ApiSchemaLanguagePackData, CallCenterInflowLanguagePackRequestModel, Option, PlatformType, TypeLanguageList } from "../../models";
import { useFunctionalityCallCenterInflow, useFunctionalityLanguage, } from "../../pages/hooks";
import { useFunctionalityInputChecker } from "../../pages/hooks/functionalityInputChecker";
import { ModalFooter } from "../Common";

type Props = {
  show?: boolean;
  handleShow: (value: boolean) => void;
  dataToEdit: ApiSchemaLanguagePackData;
  callCenterId: string;
  platformType: PlatformType;
  setLoaderProgress: any;
  setInflowStatus: any;
  setInflowSettings: any;
};

export function ModalInflowLanguagePack(props: Props): JSX.Element {
  const { show, handleShow, dataToEdit, callCenterId, platformType, setInflowStatus, setInflowSettings, setLoaderProgress, } = props;
  const [disableProp, setDisableProp] = useState<boolean>(false);
  const { register, handleSubmit, formState: { errors }, watch, setValue, trigger, clearErrors } =
    useForm<ApiSchemaCreateLanguagePack>({
      shouldUnregister: false,
      defaultValues: {
        name: dataToEdit?.name ?? "",
        flatRate: Number(dataToEdit?.flatRate ?? 0),
        languageRates: [],
      },
    });

  const [zeroRateState, setZeroRateState] = useState<any>([]);
  const [languageFlatRateError, setLanguageFlatRateError] = useState<string>("");
  const [flatRateToggle, setFlatRateToggle] = useState<boolean>(true);
  let tempOption: Option[] = [];
  let tempLanguageList: TypeLanguageList[] = [];
  const [selectedOptions, setSelectedOptions] = useState<Option[]>(tempOption);
  const [listOfLanguages, setListOfLanguages] = useState<TypeLanguageList[]>([]);
  const lastIndividualRatesRef = useRef<Map<string, number>>(new Map());
  const preFlatIdsRef = useRef<Set<string>>(new Set());
  const [showApplyAllConfirm, setShowApplyAllConfirm] = useState(false);

  const { numberFieldValidation, handleCopyPasteInvalidValue, handleLimitDecimalPlaces } = useFunctionalityInputChecker();

  const { languageList } = useFunctionalityLanguage({ plaformType: platformType, shouldLanguageGetAllInvoke: show, });
  const languageListForSelect = [
    { label: "Select All", value: "all" },
    ...languageList,
  ];
  const { onCreateCallInflowPack, onSubmit, LoadData } =
    useFunctionalityCallCenterInflow({
      callCenterId: callCenterId,
      setLoaderProgress,
    });

  const flatWatched = watch('flatRate');
  const flatValue = (flatWatched === undefined || flatWatched === null)
    ? ''
    : Number(flatWatched);

  const validateNumberOrString = (v: any) =>
    handleCopyPasteInvalidValue(typeof v === 'number' ? String(v) : (v ?? ''));

  useEffect(() => {
    let opts: Option[] = [];
    let langs: TypeLanguageList[] = [];

    if (dataToEdit?.languageRates) {
      opts = dataToEdit.languageRates.map(t => ({ value: t.languageId, label: t.languageName }));
      langs = dataToEdit.languageRates.map(o => ({
        rate: Number(o?.rate ?? 0),
        languageId: o.languageId,
        languageName: o.languageName,
        id: o.id, // keep if present
      }));
    }

    setSelectedOptions(opts);
    setListOfLanguages(langs);

    const initRates = langs.map((o, i) => ({ name: `languageRates[${i}].rate`, value: o.rate ?? 0 }));
    setZeroRateState(initRates);

    setFlatRateToggle((dataToEdit?.flatRate ?? 0) > 0);
  }, [dataToEdit]);


  useEffect(() => {
    listOfLanguages.forEach((l, i) => {
      setValue(`languageRates.${i}.languageId` as const, l.languageId);
      setValue(`languageRates.${i}.languageName` as const, l.languageName);
      setValue(`languageRates.${i}.rate` as const, Number(l.rate ?? 0));
    });
    trigger('languageRates');
  }, [listOfLanguages]);

  const onSubmitHandler = (form: ApiSchemaCreateLanguagePack) => {
    const rawFlat = Number(form.flatRate);
    const flatEnabled = flatRateToggle && Number.isFinite(rawFlat) && rawFlat > 0;
    const normalizedFlat = flatEnabled ? rawFlat : 0;

    const payload: ApiSchemaCreateLanguagePack = {
      ...form,
      name: dataToEdit.name,
      flatRate: normalizedFlat,
      IsFlatRateEnabled: flatEnabled,
      languageRates: listOfLanguages.map(t => ({
        languageId: t.languageId,
        languageName: t.languageName,
        rate: Number(t.rate ?? 0),
        id: (t as any).id,
      })) as any,
      vendorCompanyId: "",
    };

    onSubmit(payload, dataToEdit.id!).then(() => {
      handleShow(false);
      LoadData().then((d) => {
        if (d?.data) {
          setInflowSettings(d.data);
          setInflowStatus(d.data.enabled);
        }
        setLoaderProgress(topLoaderProgress.complete);
      });
    });
  };


  const handleFlatRateButtonToggle = (prev: boolean) => {
    const next = !flatRateToggle;
    setFlatRateToggle(next);

    if (!prev && next) {
      preFlatIdsRef.current = new Set(listOfLanguages.map(l => l.languageId));

      const snap = new Map<string, number>();
      listOfLanguages.forEach(l => snap.set(l.languageId, Number(l.rate ?? 0)));
      lastIndividualRatesRef.current = snap;

      clearErrors('languageRates');
      const flatNow = getFlat();
      setValue('flatRate' as const, Number(flatNow), { shouldDirty: true, shouldValidate: true });

      if (flatNow <= 0) {
        setDisableProp(true);
        setLanguageFlatRateError('flat rate must be greater than zero');
      } else {
        setDisableProp(false);
        setLanguageFlatRateError('');
        clearErrors('flatRate');
      }
      trigger('flatRate');
    }

    if (prev && !next) {
      const seed = getSeedValue();
      const snap = lastIndividualRatesRef.current;
      const preIds = preFlatIdsRef.current;

      const restored = listOfLanguages.map(l => {
        if (preIds.has(l.languageId)) {
          const backed = snap.get(l.languageId);
          const restoredVal = Number.isFinite(backed!) ? Number(backed) : 0;
          return { ...l, rate: restoredVal };
        } else {
          const current = Number(l.rate ?? 0);
          return { ...l, rate: current > 0 ? current : seed };
        }
      });

      setListOfLanguages(restored);
      restored.forEach((l, i) => setValue(`languageRates.${i}.rate` as const, Number(l.rate ?? 0), { shouldDirty: true, shouldValidate: true }));
      setValue('flatRate', 0, { shouldDirty: true, shouldValidate: true });
      clearErrors('flatRate');
      trigger('languageRates');
      setZeroRateState(restored.map((l, i) => ({ name: `languageRates[${i}].rate`, value: l.rate ?? 0 })));
    }

    if (!prev && (dataToEdit?.flatRate ?? 0) <= 0 && getFlat() <= 0) {
      setDisableProp(true);
      setLanguageFlatRateError('flat rate must be greater than zero');
    } else if (next && getFlat() > 0) {
      setDisableProp(false);
      setLanguageFlatRateError('');
    }
  };


  const handleFlatRate = (event: any) => {
    const value = event.target.value;
    if (value.length == 0) {
      setLanguageFlatRateError("")
    }
    if (Number(value) <= 0) {
      setDisableProp(true);
      setLanguageFlatRateError("flat rate must be greater than zero");
    } else {
      setDisableProp(false);
      setLanguageFlatRateError("");
    }
  };

  const onRemoveItem = (id: string) => {
    const remainingOptions = selectedOptions.filter(t => t.value !== id);
    setSelectedOptions(remainingOptions);

    const newList = listOfLanguages.filter(l => l.languageId !== id);
    setListOfLanguages(newList);

    setZeroRateState(newList.map((l, i) => ({ name: `languageRates[${i}].rate`, value: Number(l.rate ?? 0) })));
    lastIndividualRatesRef.current.delete(id);
  };

  useEffect(() => {
    const hasZeroRate = zeroRateState?.find((zero: any) => zero?.value < 0.01);
    if (hasZeroRate && zeroRateState?.length > 0 && !flatRateToggle) {
      setDisableProp(true);
    } else if (flatRateToggle && languageFlatRateError) {
      setDisableProp(true);
    } else {
      setDisableProp(false);
    }
  }, [zeroRateState, flatRateToggle, languageFlatRateError]);

  // @ts-ignore
  const handleAddLanguage = (selected: OptionsType<Option>) => {
    const selectedAll = selected.length && selected.some((o: any) => o.value === 'all');
    const selectedOptionsAny = selectedAll ? languageListForSelect.slice(1) : selected;
    setSelectedOptions(selectedOptionsAny as any);

    const seed = getSeedValue();

    const nextList: TypeLanguageList[] = (selectedOptionsAny as any).map((option: any) => {
      const existing = listOfLanguages.find(l => l.languageId === option.value);
      const existingRate = Number(existing?.rate ?? 0);
      const lastTyped = lastIndividualRatesRef.current.get(option.value);
      const lastTypedNum = Number(lastTyped ?? 0);

      const finalRate = existingRate > 0 ? existingRate
        : lastTypedNum > 0 ? lastTypedNum
          : seed;

      return { languageId: option.value, languageName: option.label, rate: finalRate, id: existing?.id };
    });

    setListOfLanguages(nextList);

    nextList.forEach((l, i) => {
      setValue(`languageRates.${i}.languageId` as const, l.languageId);
      setValue(`languageRates.${i}.languageName` as const, l.languageName);
      setValue(`languageRates.${i}.rate` as const, Number(l.rate ?? 0), { shouldDirty: true, shouldValidate: true });
    });
    trigger('languageRates');

    setZeroRateState(nextList.map((l, i) => ({ name: `languageRates[${i}].rate`, value: l.rate ?? 0 })));
  };


  type SeedMode = 'MAX' | 'FLAT' | 'ZERO' | 'CUSTOM';
  const [seedMode, setSeedMode] = useState<SeedMode>('FLAT');
  const [seedCustom, setSeedCustom] = useState<number>(0);

  const getFlat = () => {
    const watched = Number(watch('flatRate'));
    if (Number.isFinite(watched) && watched > 0) return watched;
    const saved = Number(dataToEdit?.flatRate ?? 0);
    return saved > 0 ? saved : 0;
  };

  const maxIndividual = () => {
    const nums = listOfLanguages.map(l => Number(l.rate ?? 0))
      .filter(n => Number.isFinite(n) && n > 0);
    return nums.length ? Math.max(...nums) : 0;
  };

  const getSeedValue = () => {
    switch (seedMode) {
      case 'FLAT': return Math.max(0, getFlat());
      case 'MAX': return maxIndividual();
      case 'CUSTOM': return Number(seedCustom) > 0 ? Number(seedCustom) : 0;
      case 'ZERO':
      default: return 0;
    }
  };

  const applyPresetToZeros = () => {
    const seed = getSeedValue();
    if (seed <= 0) return;

    const patched = listOfLanguages.map(l => {
      const current = Number(l.rate ?? 0);
      return current > 0 ? l : { ...l, rate: seed };
    });

    setListOfLanguages(patched);
    patched.forEach((l, i) => setValue(`languageRates.${i}.rate`, Number(l.rate ?? 0), { shouldDirty: true, shouldValidate: true }));
    trigger('languageRates');
    setZeroRateState(patched.map((l, i) => ({ name: `languageRates[${i}].rate`, value: l.rate ?? 0 })));
  };

  const applyPresetToAll = () => {
    const seed = getSeedValue();
    if (seed <= 0) return;

    const patched = listOfLanguages.map(l => ({
      ...l,
      rate: seed,
    }));

    setListOfLanguages(patched);

    patched.forEach((l, i) =>
      setValue(`languageRates.${i}.rate` as any, Number(l.rate ?? 0), {
        shouldDirty: true,
        shouldValidate: true,
      })
    );

    trigger('languageRates');

    setZeroRateState(
      patched.map((l, i) => ({
        name: `languageRates[${i}].rate`,
        value: l.rate ?? 0,
      }))
    );
  };

  const handleApplyAllConfirmed = () => {
    applyPresetToAll();
    setShowApplyAllConfirm(false);
  };



  return (
    <Modal centered show={props.show} onHide={() => props.handleShow(false)}>
      <form onSubmit={handleSubmit(onSubmitHandler)}>
        {/*@ts-ignore */}
        <Modal.Header closeButton>
          <Modal.Title>
            {dataToEdit.id ? "Update" : "Add"} Language Pack
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <div className="row">
            <div className="col-12">
              <div className="form-group">
                {/* <label htmlFor="" className="position-relative required font-sz-14"> */}
                <label htmlFor="" className="position-relative font-sz-14">
                  Package Name
                </label>
                <input
                  type="text"
                  // {...register('name', { required: true })}
                  {...register('name')}

                  disabled
                  className="form-control"
                  placeholder="Package name"
                  defaultValue={dataToEdit.name} />
                {errors.name?.type === "required" && <FormErrorMessage message="required" />}
              </div>
              <div className="fixed-height-modal">
                <div className="form-inline mb-3 flex-wrap align-items-sm-center">
                  <div className="form-group col-12 ps-0 pe-0 ">
                    <Select
                      placeholder="Select Language"
                      className="w-100"
                      options={languageListForSelect}
                      value={selectedOptions}
                      isMulti
                      isDisabled={languageList.length === 0 ? true : false}
                      closeMenuOnSelect={false}
                      components={{
                        IndicatorSeparator: () => null,
                      }}
                      filterOption={createFilter(reactSelectFilterConfig)}
                      onChange={(selected) => handleAddLanguage(selected)}
                    />
                  </div>
                </div>

                <div className="component-card rounded mb-3 shadow-none">
                  <div className="component-card-header border rounded-top ">
                    <div className="row align-items-start">
                      <div className="col-12 col-sm-6 mb-3 mb-sm-0">
                        <div>Language</div>
                        {listOfLanguages && listOfLanguages.length === 0 ? (
                          <div className="text-danger font-sz-12 ">
                            Contains no language
                          </div>
                        ) : (
                          <div className="text-muted font-sz-12 ">
                            Total {listOfLanguages.length} Selected Languages
                          </div>
                        )}
                      </div>
                      <div className="col">
                        <div className="row  flex-wrap  align-items-start mx-0">
                          <button
                            type="button"
                            className={`btn btn-toggle language-toggle-btn mt-1 ${dataToEdit.flatRate != undefined &&
                              dataToEdit.flatRate > 0 ? "active" : ""}`}
                            aria-pressed="false"
                            onClick={() =>
                              handleFlatRateButtonToggle(flatRateToggle)
                            }
                          >
                            <div className="handle"></div>
                          </button>
                          <span className="toggle-button-label  text-muted ">
                            Flat Language Rate
                          </span>
                          {flatRateToggle ? (
                            <>
                              <div className="input-group col pe-0">
                                <span className="input-group-text" id="flat-rate"> $ </span>
                                <input
                                  type="number"
                                  step="any"
                                  min="0"
                                  {...register('flatRate', { valueAsNumber: true, validate: validateNumberOrString })}
                                  className={`form-control ${languageFlatRateError ? 'field-required' : ''}`}
                                  value={flatValue}
                                  onKeyDown={numberFieldValidation}
                                  onKeyPress={(e) => { if (!/[0-9]|\./.test(e.key)) e.preventDefault(); }}
                                  onChange={(e) => {
                                    const raw = e.target.value;
                                    const val = raw === '' ? NaN : Number(raw);

                                    setValue('flatRate', isNaN(val) ? ('' as any) : val, { shouldDirty: true, shouldValidate: true });

                                    if (raw === '' || !isFinite(val) || val <= 0) {
                                      setDisableProp(true);
                                      setLanguageFlatRateError('flat rate must be greater than zero');
                                    } else {
                                      setDisableProp(false);
                                      setLanguageFlatRateError('');
                                      clearErrors('flatRate');
                                      trigger('flatRate');
                                    }
                                  }}
                                  onInput={(e) => handleLimitDecimalPlaces(e, 2)}
                                />
                                {errors.flatRate &&
                                  errors.flatRate.type === "validate" && (
                                    <FormErrorMessage message="Please enter a valid value" />
                                  )}
                                {languageFlatRateError && (
                                  <div className="font-sz-10 text-danger">
                                    {languageFlatRateError}
                                  </div>
                                )}
                              </div>
                            </>
                          ) : null}
                        </div>
                      </div>
                    </div>

                    {!flatRateToggle ? (
                      <div>
                        <div className="d-flex gap-2 mt-2 align-items-center">
                          <label className="me-2 mb-1">Default for new langs:</label>
                          <select
                            className="form-control w-auto"
                            value={seedMode}
                            onChange={(e) => setSeedMode(e.target.value as SeedMode)}
                          >
                            <option value="MAX">Use max of existing</option>
                            <option value="FLAT">Use flat rate</option>
                            <option value="ZERO">Use zero</option>
                            <option value="CUSTOM">Custom preset…</option>
                          </select>

                          {seedMode === 'CUSTOM' && (
                            <input
                              type="number"
                              min="0"
                              step="any"
                              className="form-control w-auto ms-2"
                              placeholder="Custom value"
                              value={Number(seedCustom) || ''}
                              onChange={(e) => setSeedCustom(Number(e.target.value))}
                            />
                          )}
                        </div>
                        <div className="w-100 d-flex flex-wrap align-items-end justify-content-between mt-3">

                          <div className="text-muted d-flex align-items-center">
                            New languages will start at:{' '}
                            <strong className="ms-1" style={{ color: 'var(--site-purple)' }}>
                              {getSeedValue() || 0}
                            </strong>
                          </div>

                          <div>
                            <button
                              type="button"
                              className="btn btn-outline-secondary me-2"
                              onClick={applyPresetToZeros}
                              title="Set the preset only on languages that are currently 0"
                            >
                              Apply to current 0’s
                            </button>
                            <button
                              type="button"
                              className="btn btn-outline-secondary"
                              onClick={() => setShowApplyAllConfirm(prev => !prev)}
                              title="Set the preset to all languages"
                            >
                              Apply to all
                            </button>
                          </div>
                        </div>

                        <div
                          className={`apply-all-confirm-wrapper ${showApplyAllConfirm ? 'is-open' : ''
                            }`}
                        >
                          <div className="apply-all-confirm-inner alert alert-warning d-flex flex-column flex-sm-row align-items-sm-center justify-content-between">
                            <div className="mb-2 mb-sm-0">
                              <strong>Are you sure?</strong>{' '}
                              <p className="mb-0">
                                This will overwrite the rate of all selected languages to{' '}
                                <strong>${getSeedValue() || 0}</strong>.
                              </p>
                            </div>

                            <div className="d-flex">
                              <button
                                type="button"
                                className="btn btn-sm btn-danger me-2"
                                onClick={handleApplyAllConfirmed}
                                disabled={getSeedValue() <= 0}
                              >
                                Yes
                              </button>
                              <button
                                type="button"
                                className="btn btn-sm btn-outline-secondary"
                                onClick={() => setShowApplyAllConfirm(false)}
                              >
                                Cancel
                              </button>
                            </div>
                          </div>
                        </div>

                      </div>
                    ) : null}

                  </div>
                  <div className="component-card-body rounded-bottom border-start border-end border-bottom">
                    <ul className="list-group">
                      {listOfLanguages.map((language, index) => (
                        <li className="list-group-item" key={language.languageId}>
                          <div className="row">
                            <div className="col-12 col-sm-6 col-md-6">
                              <p className="word-wrap mb-sm-0 mb-2 pt-2">
                                {language.languageName}
                              </p>
                            </div>
                            <div className="col-12 col-sm-5 col-md-5 pe-sm-0 mb-2 mb-sm-0">
                              {flatRateToggle ? null : (
                                <span>
                                  <div className="input-group">
                                    <span className="input-group-text" id="flat-rate">$</span>
                                    <input
                                      type="hidden"
                                      className="form-control"
                                      //@ts-ignore
                                      {...register(`languageRates[${index}].languageName`, {
                                        required: flatRateToggle ? true : false,
                                      })}
                                      aria-describedby="languageName"
                                      defaultValue={language.languageName} />

                                    <input
                                      type="hidden"
                                      className="form-control"
                                      //@ts-ignore 
                                      {...register(`languageRates[${index}].languageId`, {
                                        required: flatRateToggle ? true : false,
                                      })}
                                      aria-describedby="languageId"
                                      defaultValue={language.languageId} />

                                    <input
                                      type="number"
                                      min="0"
                                      step="any"
                                      // @ts-ignore
                                      {...register(`languageRates[${index}].rate`, { required: true, validate: validateNumberOrString })}
                                      className="form-control"
                                      value={Number(language.rate ?? 0)}
                                      style={{ border: !language.rate ? '1px solid red' : '' }}
                                      onKeyDown={numberFieldValidation}
                                      onKeyPress={(e) => { if (!/[0-9]|\./.test(e.key)) e.preventDefault(); }}
                                      onChange={(e) => {
                                        const value = Number(e.target.value);
                                        setListOfLanguages(prev => prev.map(item =>
                                          item.languageId === language.languageId ? { ...item, rate: value } : item
                                        ));
                                        setValue(`languageRates.${index}.rate` as any, value, { shouldDirty: true, shouldValidate: true });
                                        trigger(`languageRates.${index}.rate` as any);
                                        lastIndividualRatesRef.current.set(language.languageId, value);
                                      }}
                                      onInput={(e) => handleLimitDecimalPlaces(e, 2)}
                                    />

                                    {errors.languageRates?.[index]?.rate &&
                                      errors.languageRates?.[index]?.rate
                                        ?.type === "required" && (
                                        <FormErrorMessage message="required" />
                                      )}
                                    {errors.languageRates?.[index]?.rate &&
                                      errors.languageRates?.[index]?.rate
                                        ?.type === "validate" && (
                                        <FormErrorMessage message="Please enter a valid value" />
                                      )}
                                  </div>
                                </span>
                              )}
                            </div>
                            <span className="col-12 col-sm-1 col-md-1 px-sm-2 text-start text-sm-center ">
                              <button
                                type="button"
                                className="btn btn-link px-0 text-site svg-color"
                                onClick={() => {
                                  onRemoveItem(
                                    language.languageId
                                  );
                                }}
                              >
                                <span >
                                  <AssetSvg.Delete />
                                </span>
                              </button>
                            </span>
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Modal.Body>

        <Modal.Footer>
          <ModalFooter
            handleShow={handleShow}
            zeroRateLanguagePack={disableProp}
          />
        </Modal.Footer>
      </form>
    </Modal>
  );
}
